import { useState, type ReactNode } from 'react';
import { Size, t } from '@bms2/shared';
import { Textarea } from '../controls';
import { Modal } from './Modal';
import { ModalField } from './ModalShell';
import styles from './modal.module.css';

/**
 * 🔒 The reason window (piece 5, decision 2870): a required reason, then one act
 * such as Void, Reject, Write Off, Dispute, Withdraw or Reopen. It is the one
 * home of the sixteen windows that each held their reason in the page.
 *
 * The window holds its own text. A refused act leaves it open with the text as
 * typed, because whoever opened it closes it only when the act took; a closed
 * window renders nothing, so the text dies with it and the next opening starts
 * empty. Two windows on one page therefore never share a reason.
 *
 * The refusal of an act pressed in this opening reads inside the window, above
 * the Footer: the open dialog makes the page behind it inert, so a banner there
 * is out of reach of a screen reader and off screen on a phone.
 */
export const reasonWindowDocumentedStates = ['empty', 'busy'] as const;

export interface ReasonWindowProps {
  open: boolean;
  title: string;
  /** The reason box's label, which the screen reader reads too. */
  label: string;
  /** The Primary's label: the act the reason is for. */
  actLabel: string;
  /**
   * Sends the trimmed reason. The act's own promise is handed to the Footer, so
   * the Primary and the ways out hold while it is in flight (decision 2840).
   */
  onAct: (reason: string) => unknown;
  /** The page's own write is in flight: the Primary stays disabled. */
  busy: boolean;
  onCancel: () => void;
  dataModal: string;
  /** A line above the reason saying what the act does. */
  hint?: string;
  /** The reason box's form name, the handle a suite types into. */
  name?: string;
  /** A further field the act needs beside the reason, such as who authorized it. */
  children?: ReactNode;
  /** False while that further field is not yet answered; the Primary waits for it too. */
  ready?: boolean;
  /** 🔒 Why the window is not ready, the sentence the held Primary shows while `ready` is false (decision 0559). */
  notReadyReason?: string;
  /**
   * 🔒 The server's refusal of the act, which the page also shows behind the window. The window draws it only
   * once its own Primary was pressed and the act has answered, so a refusal standing before it opened never shows.
   */
  refusal?: ReactNode;
}

export function ReasonWindow(props: ReasonWindowProps) {
  // 🔒 Rendering nothing while closed is what clears the text (decision 2870; T2135).
  return props.open ? <OpenReasonWindow {...props} /> : null;
}

function OpenReasonWindow({ title, label, actLabel, onAct, busy, onCancel, dataModal, hint, name = 'reason', children, ready = true, notReadyReason, refusal = null }: ReasonWindowProps) {
  const [reason, setReason] = useState('');
  const [pressed, setPressed] = useState(false);
  const trimmed = reason.trim();
  // The refusal shows once this opening's own act has answered, never one that stood before it opened.
  const refused = pressed && !busy && Boolean(refusal);
  const act = () => {
    setPressed(true);
    return onAct(trimmed);
  };
  // 🔒 A held Primary names why (decision 0559): the write in flight first, then the blank reason, then the caller's own field.
  const heldBecause = busy ? t('expenses.busy') : !trimmed ? t('modal.reason.needsReason') : !ready ? notReadyReason : undefined;
  return (
    <Modal
      open
      title={title}
      onCancel={onCancel}
      dataModal={dataModal}
      actions={[
        { kind: 'primary', label: actLabel, onAct: act, disabled: busy || !trimmed || !ready, ...(heldBecause === undefined ? {} : { disabledReason: heldBecause }) },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {hint ? <p className={styles.fieldLabel}>{hint}</p> : null}
      <ModalField label={label} required>
        <Textarea size={Size.Large} label={label} labelHidden value={reason} onChange={setReason} name={name} />
      </ModalField>
      {children}
      {/* 🔒 A refusal reads inside the window above the Footer (piece 5, *Reason window*; decision 2170). */}
      {refused ? (
        <p className={styles.reasonRefusal} role="alert" data-reason-refusal="true">
          {refusal}
        </p>
      ) : null}
    </Modal>
  );
}
